import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSEO } from "@/hooks/use-seo";
import { useAuth } from "@/hooks/use-auth";
import { useSubmitLock } from "@/hooks/use-submit-lock";
import { getUtmData } from "@/hooks/use-utm";
import { analyticsVisitorFields } from "@/lib/visitor-link";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AnimatedSection } from "@/hooks/use-scroll-animation";
import { InvestorPackageModal } from "@/components/investor-package-modal";
import { SecuritiesDisclaimer } from "@/components/securities-disclaimer";
import { WhatsAppButton, whatsAppMessageForAudience } from "@/components/whatsapp-button";
import { ContentGalleryGrid } from "@/components/content-gallery-grid";
import {
  HomeHeroLoops,
  HomeHeroLoopCaption,
  buildHomeHeroSlides,
} from "@/components/home-hero-loops";
import { pickProjectImage } from "@/lib/project-image";
import { formatCurrency } from "@/lib/format-currency";
import { BRAND } from "@/lib/brand-config";
import { knownAccountEmail } from "@shared/account-email";
import type { PublicGalleryItem } from "@shared/content-gallery";
import { AeoAnswer } from "@/components/aeo-block";
import {
  HOME_ANSWER,
  HOME_DEFINITION,
  HOME_INVESTMENT_AS_OF,
  HOME_META_DESCRIPTION,
  HOME_QUESTION,
  SITE_FOOTER_DISCLAIMER,
  buildHomeOrganizationJsonLd,
} from "@shared/aeo-copy";
import {
  ArrowRight,
  CheckCircle,
  Users,
  Building2,
  ShieldCheck,
  Flame,
  Clock,
  FileText,
  Home as HomeIcon,
  AlertTriangle,
} from "lucide-react";
import { isApproved } from "@/lib/project-labels";

// ---------------------------------------------------------------------------
// Team grid: count-aware responsive width so 4-6 profiles stay balanced.
// Mobile = 1 col, tablet = 2 cols. Desktop = 2 cols for exactly 4 (clean 2x2,
// no orphan) and 3 cols otherwise (5 -> 3+2, 6 -> 3+3), centered via the
// flex-wrap container. Any count adjusts automatically with no further code.
// ---------------------------------------------------------------------------

function teamCardWidthClass(count: number): string {
  const lgWidth =
    count === 4 ? "lg:w-[calc(50%-0.75rem)]" : "lg:w-[calc(33.333%-1rem)]";
  return `w-full sm:w-[calc(50%-0.75rem)] ${lgWidth} min-w-0`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectTeaser {
  id: string;
  title: string;
  slug: string;
  subtitle: string | null;
  location: string;
  city?: string;
  state?: string;
  investmentMinimum: number;
  investmentType: string;
  category: string;
  status: string;
  i956fApprovedAt?: string | null;
  coverImageUrl: string | null;
  marketingImageDerivatives?: unknown;
  heroVideoUrl?: string | null;
  heroVideoPosterUrl?: string | null;
  heroVideoContentType?: string | null;
  eb5RaiseTarget: number | null;
  currentRaise: number | null;
}

interface TeamMember {
  id: string;
  name: string;
  title: string;
  bio: string;
  headshot: string;
  linkedin?: string;
}

interface PublicTeamResponse {
  corporate: TeamMember[];
  divisions: { id: string; slug: string; label: string; experts: TeamMember[] }[];
}

interface AvailabilitySlot {
  start: string;
  end: string;
}

interface AvailabilityResponse {
  slots: AvailabilitySlot[];
  message?: string;
}

// ---------------------------------------------------------------------------
// Per-slug marketing config (verbatim approved copy per project)
// ---------------------------------------------------------------------------

interface ProjectStat {
  value: string;
  label: string;
}

interface ProjectMarketingCopy {
  headline: string;
  subhead: string;
  featured?: boolean;
  // Deal-specific spotlight content. These only render for the project they
  // belong to; projects without a config fall back to generic stats derived
  // from their own real data (see deriveGenericStats).
  stats?: ProjectStat[];
  highlight?: ProjectStat;
  positionsNote?: string;
}

const PROJECT_MARKETING_CONFIG: Record<string, ProjectMarketingCopy> = {
  "american-energy": {
    headline: "Natural Gas. National Demand. Your Green Card.",
    subhead:
      "American Energy EB-5 is a USCIS-approved rural project financing new natural gas wells in Louisiana's Haynesville Shale, backed by a sponsor with more than 40 years in energy development.",
    featured: true,
    stats: [
      { value: "I-956F Approved", label: "USCIS-Approved Rural Project" },
      { value: "$800,000", label: "Rural TEA Investment Level" },
      { value: "1st Lien Position", label: "EB-5 Capital Is Repaid First" },
      { value: "20% Visa Set-Aside", label: "Reserved Visas and Priority Processing" },
    ],
    highlight: { value: "40+ Year Track Record", label: "Sponsor Experience in Oil and Gas" },
    positionsNote: "Limited to 28 investor positions.",
  },
};
// The live record's slug is "american-energy-eb-5-llc"; without this alias the
// featured pick fell through to the first project (Marathon Key).
PROJECT_MARKETING_CONFIG["american-energy-eb-5-llc"] = PROJECT_MARKETING_CONFIG["american-energy"];

// ---------------------------------------------------------------------------
// FAQ (compliant)
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    id: "what-is-eb5",
    question: "What is the EB-5 Investor Visa program?",
    answer:
      "The EB-5 program allows international families to obtain U.S. permanent residency (a green card) by investing in a USCIS-approved project that creates at least 10 full-time jobs for U.S. workers. Rural Targeted Employment Area projects qualify at $800,000. The program is administered by U.S. Citizenship and Immigration Services.",
  },
  {
    id: "family-green-cards",
    question: "Can my family get green cards too?",
    answer:
      "Yes. Your spouse and unmarried children under 21 are included as derivative beneficiaries on your petition and can receive their own conditional green cards. The Child Status Protection Act may help protect children who could age out during processing. Each family member files separately at the relevant stage.",
  },
  {
    id: "how-long",
    question: "How long does the EB-5 process take?",
    answer:
      "The estimated total timeline is 3 to 7 or more years, depending on your country of birth and investment category. I-526E petition processing is estimated at 25 to 52 months, with optional premium processing targeting adjudication in as fast as 120 days. Visa availability and consular processing add additional estimated time. Individual cases vary. See the timeline calculator for an estimate based on your situation.",
  },
  {
    id: "investment-risk",
    question: "Is my investment at risk?",
    answer:
      "Yes. All EB-5 investments involve risk, including the possible loss of capital. The program specifically requires that your investment capital be at risk, which means there is no guarantee of return. You should review the full offering documents carefully with your immigration and securities attorney before investing. Past project performance does not guarantee future results.",
  },
  {
    id: "capital-after-conditions",
    question: "What happens to my investment capital after my green card conditions are removed?",
    answer:
      "Repayment of your investment capital is governed solely by the terms of each project's offering documents and is not guaranteed. The timeline and conditions for capital return vary by project and are set out in the subscription agreement and offering memorandum. Consult with your securities attorney to fully understand the repayment terms of any specific project before investing.",
  },
];

// ---------------------------------------------------------------------------
// Utility: format a slot date for display ("Tuesday, June 10 at 2:00 PM ET")
// ---------------------------------------------------------------------------

function formatSlot(isoString: string): string {
  try {
    const d = new Date(isoString);
    const day = d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      timeZone: "America/New_York",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h12",
      timeZone: "America/New_York",
    });
    return `${day} at ${time} ET`;
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// HeadshotCircle (reused from about.tsx pattern)
// ---------------------------------------------------------------------------

function HeadshotCircle({
  src,
  name,
  className,
}: {
  src: string;
  name: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const initials = name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className={`rounded-full overflow-hidden bg-muted border-2 border-primary/20 flex items-center justify-center shrink-0 ${className ?? ""}`}
    >
      {failed ? (
        <span className="text-xl font-bold text-primary/70">{initials}</span>
      ) : (
        <img
          src={src}
          alt={`Headshot of ${name}`}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Home() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [, navigate] = useLocation();
  const isPublicOverride =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("public");
  const shouldRedirectAdmin =
    !isAuthLoading && user?.role === "admin" && !isPublicOverride;

  useEffect(() => {
    if (shouldRedirectAdmin) {
      navigate("/admin", { replace: true });
    }
  }, [shouldRedirectAdmin, navigate]);

  // Supporting fix #1: updated title and description, no "independent", "unbiased", or "advisory"
  useSEO({
    title: "EB-5 Rural Projects and Green Card Guidance | Veras Capital",
    description: HOME_META_DESCRIPTION,
    path: "/",
    ogTitle: "EB-5 Investment Platform: Vetted Rural Projects & Green Card Guidance",
    ogDescription: HOME_META_DESCRIPTION,
    jsonLd: [
      buildHomeOrganizationJsonLd(),
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: BRAND.siteName,
        url: BRAND.domainUrl,
      },
    ],
  });

  // Ref for scrolling to the Featured Project section
  const featuredRef = useRef<HTMLElement>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);

  // Data
  const { data: projects } = useQuery<ProjectTeaser[]>({
    queryKey: ["/api/projects"],
  });

  // Hero loops: which project's motion loop is on screen. Auto-rotation
  // stops once the visitor picks a project with the dots.
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [heroAutoRotate, setHeroAutoRotate] = useState(true);
  const advanceHeroSlide = useCallback(() => {
    setHeroSlideIndex((index) => index + 1);
  }, []);
  const selectHeroSlide = useCallback((index: number) => {
    setHeroSlideIndex(index);
    setHeroAutoRotate(false);
  }, []);

  // One hero loop per project, featured project first (same pick as the
  // spotlight below). Projects without a hero video fall back to their
  // cover image; nothing is invented.
  const heroSlides = useMemo(() => {
    const featuredSlug =
      projects?.find((p) => PROJECT_MARKETING_CONFIG[p.slug]?.featured)?.slug ??
      projects?.[0]?.slug ??
      null;
    return buildHomeHeroSlides(projects, featuredSlug);
  }, [projects]);

  // Preload the first slide's own poster for LCP — never the retired wellpad.
  useEffect(() => {
    const poster = heroSlides[0]?.poster;
    if (!poster) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = poster;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [heroSlides]);

  const { data: teamData } = useQuery<PublicTeamResponse>({
    queryKey: ["/api/public/team"],
  });

  const { data: galleryData } = useQuery<{ items: PublicGalleryItem[] }>({
    queryKey: ["/api/public/content-gallery"],
  });
  const galleryPreview = (galleryData?.items ?? []).slice(0, 3);

  // The homepage shows the single next available consultation slot. The
  // availability endpoint requires a start date (YYYY-MM-DD) + timezone, so we
  // pass today's date in the visitor's timezone and look two weeks ahead.
  // Omitting these produced a 400 on every homepage load (the bulk of our
  // Sentry "date parameter required" noise).
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const browserTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";

  const { data: availabilityData } = useQuery<AvailabilityResponse>({
    queryKey: ["/api/scheduling/availability", todayStr, browserTimezone],
    queryFn: async () => {
      const res = await fetch(
        `/api/scheduling/availability?date=${todayStr}&timezone=${encodeURIComponent(browserTimezone)}&days=14`,
      );
      if (!res.ok) throw new Error("Failed to fetch availability");
      return res.json();
    },
  });

  if (shouldRedirectAdmin || (isAuthLoading && !isPublicOverride)) {
    return <div className="min-h-screen" data-testid="home-admin-redirect" />;
  }

  // Determine the single project to spotlight on the homepage
  const activeProjects = projects ?? [];

  // Resolve a project's public page URL by slug. Returns the project page href
  // when an active project matches, otherwise null (caller scrolls to Section 2).
  function getFeaturedProjectHref(slug: string): string | null {
    const match = activeProjects.find((p) => p.slug === slug);
    if (match) return `/projects/${match.slug}`;
    // TODO: build a dedicated public project landing page for /invest/:slug
    return null;
  }

  // Determine what to show in the featured project slot. The "featured" project
  // is the one whose slug is in PROJECT_MARKETING_CONFIG with featured: true,
  // or (if none matches) the first active project.
  const featuredProject =
    activeProjects.find((p) => PROJECT_MARKETING_CONFIG[p.slug]?.featured) ??
    activeProjects[0] ??
    null;

  const featuredCopy = featuredProject
    ? (PROJECT_MARKETING_CONFIG[featuredProject.slug] ?? null)
    : null;

  // Link the spotlight + hero CTA to whichever project is actually featured,
  // not a hard-coded slug — otherwise a different live project would route
  // investors to the wrong project page.
  const featuredProjectHref = featuredProject
    ? getFeaturedProjectHref(featuredProject.slug)
    : null;

  const nextSlot =
    availabilityData?.slots && availabilityData.slots.length > 0
      ? formatSlot(availabilityData.slots[0].start)
      : null;

  const principals = teamData?.corporate ?? [];

  function scrollToFeatured() {
    featuredRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="flex flex-col" data-testid="page-home">

      {/* ================================================================
          SECTION 1: HERO
          ================================================================ */}
      <section
        className="relative flex min-h-[85vh] flex-col justify-center text-white"
        data-testid="section-hero"
        aria-label="Hero"
      >
        {/* Background: solid navy base. Project motion loops paint on top
            once the payload arrives — the retired wellpad photo is never
            rendered here, so crossfades cannot expose it. Overflow is
            clipped on the media layer only so the copy/CTAs are never cut
            off. A light scrim + localized text band keep the white
            headline at WCAG AA. */}
        <div className="absolute inset-0 z-0 overflow-hidden bg-[#0A2540]" aria-hidden="true">
          {heroSlides.length > 0 ? (
            <HomeHeroLoops
              slides={heroSlides}
              activeIndex={heroSlideIndex}
              onAdvance={advanceHeroSlide}
              autoRotate={heroAutoRotate}
            />
          ) : null}
          <div className="absolute inset-0 z-30 bg-gradient-to-b from-[#0A2540]/40 via-[#0A2540]/15 to-[#0A2540]/55" />
          <div className="pointer-events-none absolute inset-x-0 top-[28%] z-30 h-[38%] bg-gradient-to-b from-transparent via-[#0A2540]/45 to-transparent" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-16 sm:py-20 text-center">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 max-w-4xl mx-auto [text-shadow:0_2px_28px_rgba(10,37,64,0.9),0_1px_3px_rgba(10,37,64,0.95)]"
            data-testid="text-hero-heading"
          >
            A U.S. green card for your family, through a vetted EB-5 investment.
          </h1>
          <p
            className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-10 [text-shadow:0_1px_16px_rgba(10,37,64,0.85)]"
            data-testid="text-hero-subhead"
          >
            {HOME_DEFINITION}
          </p>
          {featuredProject && PROJECT_MARKETING_CONFIG[featuredProject.slug]?.featured ? (
            <p
              className="text-base sm:text-lg font-semibold text-[#E5C97A] max-w-2xl mx-auto -mt-6 mb-10 [text-shadow:0_1px_16px_rgba(10,37,64,0.85)]"
              data-testid="text-hero-featured-line"
            >
              Natural gas wells in rural Louisiana. I-956F approved. $800,000 rural EB-5 level.
            </p>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto">
            <Button
              size="lg"
              className="btn-gold-3d text-brand-dark-bg font-semibold h-14 px-8 text-base w-full sm:w-auto"
              onClick={() => {
                if (featuredProjectHref) {
                  navigate(featuredProjectHref);
                } else {
                  scrollToFeatured();
                }
              }}
              data-testid="button-hero-view-project"
            >
              {featuredProject && PROJECT_MARKETING_CONFIG[featuredProject.slug]?.featured
                ? "See American Energy"
                : "View Current Project"}
            </Button>
            <Link href="/schedule-call" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white bg-white/15 text-white hover:bg-white/25 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent h-14 px-8 text-base w-full sm:w-auto"
                data-testid="button-hero-schedule"
              >
                Schedule a Call
              </Button>
            </Link>
          </div>

          <div className="mt-6">
            <Link href="/agents/apply">
              <span
                className="text-sm text-white/60 hover:text-white/90 transition-colors inline-flex items-center gap-1"
                data-testid="link-hero-referral-partner"
              >
                Referral partner? Refer investors and earn
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </div>

          <HomeHeroLoopCaption
            slides={heroSlides}
            activeIndex={heroSlideIndex}
            onSelect={selectHeroSlide}
          />
        </div>

        {/* Trust strip */}
        <div
          className="relative z-10 border-t border-white/15 bg-[#0A2540]/80 backdrop-blur-sm"
          data-testid="trust-strip"
        >
          <div className="container mx-auto px-4 py-4 sm:py-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              {[
                { label: "I-956F Approved Project" },
                { label: "Rural TEA: $800,000" },
                { label: "20% Visa Set-Aside" },
                { label: "Serving Investors in 30+ Countries" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`trust-item-${item.label.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  <CheckCircle className="h-4 w-4 text-[#C9A84C] shrink-0" aria-hidden="true" />
                  <span className="text-white/90 font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-background" data-testid="section-home-aeo">
        <div className="container mx-auto px-4 max-w-3xl">
          <AeoAnswer
            question={HOME_QUESTION}
            answer={HOME_ANSWER}
            asOfLines={[HOME_INVESTMENT_AS_OF]}
            headingClassName="text-2xl sm:text-3xl font-bold text-foreground mb-4"
            textClassName="text-muted-foreground leading-relaxed mb-4"
            asOfClassName="text-sm text-muted-foreground"
          />
        </div>
      </section>

      {/* ================================================================
          SECTION 2: FEATURED PROJECT
          ================================================================ */}
      <section
        id="featured-project"
        ref={featuredRef}
        className="py-14 sm:py-20 bg-background"
        data-testid="section-featured-project"
        aria-label="Featured Project"
      >
        <div className="container mx-auto px-4">
          {featuredProject ? (
            <AnimatedSection animation="fade-up">
              <FeaturedProjectBlock
                project={featuredProject}
                copy={featuredCopy}
                onRequestPackage={() => setModalOpen(true)}
                projectPageHref={featuredProjectHref}
              />
            </AnimatedSection>
          ) : (
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-lg mx-auto py-10">
                <Badge className="mb-3">Coming Soon</Badge>
                <h2 className="text-2xl font-bold mb-2">Projects Launching Soon</h2>
                <p className="text-muted-foreground mb-6">
                  We are preparing our next EB-5 offering. Contact us to be notified first.
                </p>
                <Button
                  className="btn-gold-3d text-brand-dark-bg font-semibold"
                  onClick={() => setModalOpen(true)}
                  data-testid="button-no-projects-contact"
                >
                  Request Information
                </Button>
              </div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* ================================================================
          SECTION 3: WHY RURAL EB-5, WHY NOW
          ================================================================ */}
      <section
        className="relative py-16 sm:py-24 text-white overflow-hidden"
        data-testid="section-why-rural"
        aria-label="Why rural EB-5"
      >
        <div className="absolute inset-0 bg-[#0A2540]" aria-hidden="true">
          <img
            src="/images/home/louisiana-forest.webp"
            alt=""
            role="presentation"
            width={1920}
            height={800}
            className="w-full h-full object-cover opacity-25"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/80 to-[#0A2540]/95" />
        </div>

        <div className="relative z-10 container mx-auto px-4">
          <AnimatedSection animation="fade-up" className="text-center mb-12">
            <p className="text-[#C9A84C] text-sm font-semibold uppercase tracking-widest mb-2">
              The Rural Advantage
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
              Why many families look at rural EB-5 first
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: "Rural visas have their own line",
                body: "20% of EB-5 visas each year are set aside for rural projects. In recent Visa Bulletins the rural line has been Current, including for China and India, where the unreserved line has a long wait. This can change from month to month. Check the live Visa Bulletin.",
              },
              {
                title: "Priority processing",
                body: "Rural petitions receive priority processing from USCIS. Optional premium processing targets adjudication of your I-526E in as fast as 120 days.",
              },
              {
                title: "A lower investment level",
                body: "Rural Targeted Employment Area projects qualify at $800,000 instead of $1,050,000, the lowest threshold the program offers.",
              },
            ].map((col, i) => (
              <AnimatedSection key={col.title} animation="fade-up" delay={i * 100} className="min-w-0">
                <div
                  className="text-center sm:text-left"
                  data-testid={`why-rural-col-${i + 1}`}
                >
                  <CheckCircle className="h-8 w-8 text-[#C9A84C] mx-auto sm:mx-0 mb-4" aria-hidden="true" />
                  <h3 className="text-lg font-bold mb-3">{col.title}</h3>
                  <p className="text-white/75 leading-relaxed">{col.body}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fade-up" delay={300} className="text-center">
            <Link href="/timeline-calculator">
              <span
                className="text-[#C9A84C] font-medium hover:text-[#D9BD6C] transition-colors inline-flex items-center gap-1"
                data-testid="link-why-rural-timeline"
              >
                See the estimated timeline for your country
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================
          SECTION 4: THE PROCESS
          ================================================================ */}
      <section
        className="py-16 sm:py-24 bg-background"
        data-testid="section-process"
        aria-label="The process"
      >
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-up" className="text-center mb-12">
            <p className="text-[#C9A84C] text-sm font-semibold uppercase tracking-widest mb-2">
              How It Works
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
              From first call to green card
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12">
            {[
              {
                num: 1,
                icon: Clock,
                title: "Talk with a specialist",
                body: "A free 30-minute consultation to understand your goals, your country's timeline, and whether EB-5 fits your family. No commitment and no pressure.",
              },
              {
                num: 2,
                icon: FileText,
                title: "Verify and prepare",
                body: "We confirm your eligibility and help you organize your source-of-funds documentation, the most demanding part of any EB-5 petition, alongside your immigration attorney.",
              },
              {
                num: 3,
                icon: ShieldCheck,
                title: "Invest and file",
                body: "Review the full offering materials, complete your subscription, and fund escrow. Your attorney files your I-526E petition with USCIS.",
              },
              {
                num: 4,
                icon: HomeIcon,
                title: "Arrive and settle",
                body: "Upon approval, you and your family receive conditional green cards. We stay with you through I-829 and the removal of conditions.",
              },
            ].map((step, i) => {
              const StepIcon = step.icon;
              return (
                <AnimatedSection key={step.num} animation="fade-up" delay={i * 80} className="min-w-0">
                  <div
                    className="flex flex-col items-center text-center sm:items-start sm:text-left"
                    data-testid={`process-step-${step.num}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 rounded-full bg-[#C9A84C] text-[#0A2540] flex items-center justify-center font-bold text-lg shrink-0">
                        {step.num}
                      </div>
                      <StepIcon className="h-5 w-5 text-[#C9A84C]" aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{step.body}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>

          <AnimatedSection animation="fade-up" delay={400} className="text-center">
            <Link href="/schedule-call">
              <Button
                size="lg"
                className="btn-gold-3d text-brand-dark-bg font-semibold"
                data-testid="button-process-schedule"
              >
                Start with a free consultation
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================
          SECTION 5: TEAM AND PLATFORM
          ================================================================ */}
      <section
        className="py-16 sm:py-24 bg-muted/30"
        data-testid="section-team"
        aria-label="Team and platform"
      >
        <div className="container mx-auto px-4">
          {/* 5a: Principals */}
          <AnimatedSection animation="fade-up" className="text-center mb-10">
            <p className="text-[#C9A84C] text-sm font-semibold uppercase tracking-widest mb-2">
              The Team
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
              The team and the platform behind your investment
            </h2>
          </AnimatedSection>

          {principals.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-6 mb-10 max-w-4xl mx-auto">
              {principals.map((member, i) => (
                <AnimatedSection key={member.id} animation="fade-up" delay={i * 80} className={teamCardWidthClass(principals.length)}>
                  <Card className="glass-card text-center p-6 h-full" data-testid={`card-team-${member.id}`}>
                    <HeadshotCircle
                      src={member.headshot}
                      name={member.name}
                      className="w-24 h-24 mx-auto mb-4"
                    />
                    <h3 className="font-semibold text-base" data-testid={`text-team-name-${member.id}`}>
                      {member.name}
                    </h3>
                    <p className="text-sm text-primary font-medium mt-0.5" data-testid={`text-team-title-${member.id}`}>
                      {member.title}
                    </p>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-6 mb-10 max-w-4xl mx-auto">
              {["McFadyen Anderson", "Alexandra Loveless", "Richard Pennell"].map((name, i) => (
                <AnimatedSection key={name} animation="fade-up" delay={i * 80} className={teamCardWidthClass(3)}>
                  <Card className="glass-card text-center p-6 h-full" data-testid={`card-team-placeholder-${i}`}>
                    <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Users className="h-10 w-10 text-muted-foreground/50" aria-hidden="true" />
                    </div>
                    <h3 className="font-semibold text-base">{name}</h3>
                    <p className="text-sm text-primary font-medium mt-0.5">Principal and Co-Founder</p>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          )}

          <AnimatedSection animation="fade-up" delay={240}>
            <div className="max-w-2xl mx-auto">
              <Card className="glass-card">
                <CardContent className="p-6 sm:p-8">
                  <p className="text-muted-foreground leading-relaxed text-center" data-testid="text-team-description">
                    Veras Capital structures, vets, and markets the EB-5 offerings on this platform. Our team works across immigration, securities, and deal structuring, and we are compensated by project sponsors. We believe the right way to earn trust is full transparency about our role, rigorous third-party professionals on every deal, and compliance built into every step.
                  </p>
                </CardContent>
              </Card>
            </div>
          </AnimatedSection>

          {/* 5b: Professionals row */}
          <AnimatedSection animation="fade-up" delay={320} className="mt-10">
            <div className="max-w-3xl mx-auto">
              <p className="text-center text-sm text-muted-foreground mb-4" data-testid="text-professionals-label">
                Every Veras offering is supported by qualified professionals:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  {
                    label: "USCIS-Designated Regional Center Sponsor",
                    icon: ShieldCheck,
                    placeholder: "// TODO: supply firm name and logo",
                  },
                  {
                    label: "Immigration and Securities Counsel",
                    icon: FileText,
                    placeholder: "// TODO: supply firm name and logo",
                  },
                  {
                    label: "Third-Party Economic and Job Creation Analysis",
                    icon: Building2,
                    placeholder: "// TODO: supply firm name and logo",
                  },
                  {
                    label: "Escrow at a Regulated U.S. Bank",
                    icon: ShieldCheck,
                    placeholder: "// TODO: supply firm name and logo",
                  },
                ].map((prof, i) => {
                  const ProfIcon = prof.icon;
                  return (
                    <div
                      key={i}
                      className="rounded-xl border border-border/60 bg-muted/30 p-4 text-center"
                      data-testid={`professional-slot-${i}`}
                      title={prof.placeholder}
                    >
                      <ProfIcon className="h-6 w-6 text-[#C9A84C] mx-auto mb-2" aria-hidden="true" />
                      <p className="text-xs text-muted-foreground leading-snug">{prof.label}</p>
                      {/* {prof.placeholder} */}
                    </div>
                  );
                })}
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" delay={400} className="text-center mt-8">
            <Link href="/about">
              <span
                className="text-[#C9A84C] font-medium hover:text-[#D9BD6C] transition-colors inline-flex items-center gap-1"
                data-testid="link-meet-team"
              >
                Meet the full team
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================
          SECTION 6: TOOLS (lead capture row)
          ================================================================ */}
      <section
        className="py-16 sm:py-24 bg-background overflow-x-hidden"
        data-testid="section-tools"
        aria-label="Tools"
      >
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fade-up" className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
              Tools that answer your biggest questions
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Card 1: Funds Assessment */}
            <AnimatedSection animation="fade-up" delay={0} className="w-full min-w-0">
              <Card className="glass-card h-full flex flex-col" data-testid="card-tool-funds">
                <div
                  className="h-24 sm:h-36 bg-[#0A2540] rounded-t-md flex items-center justify-center"
                  aria-hidden="true"
                >
                  <ShieldCheck className="h-11 w-11 sm:h-14 sm:w-14 text-[#C9A84C]" />
                </div>
                <CardContent className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-base mb-2">Talk to an EB-5 attorney</h3>
                  <p className="text-sm text-muted-foreground flex-1 mb-4">
                    Veras does not prepare source-of-funds files. Your attorney does. We can connect you with an experienced EB-5 attorney. The attorney works for you, not Veras.
                  </p>
                  <Link href="/schedule-call">
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid="button-tool-funds"
                    >
                      Talk to an EB-5 attorney
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </AnimatedSection>

            {/* Card 2: Timeline Calculator */}
            <AnimatedSection animation="fade-up" delay={100} className="w-full min-w-0">
              <Card className="glass-card h-full flex flex-col" data-testid="card-tool-timeline">
                <div
                  className="h-24 sm:h-36 bg-[#0A2540] rounded-t-md flex items-center justify-center"
                  aria-hidden="true"
                >
                  <Clock className="h-11 w-11 sm:h-14 sm:w-14 text-[#C9A84C]" />
                </div>
                <CardContent className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-base mb-2">How long will it take for your country?</h3>
                  <p className="text-sm text-muted-foreground flex-1 mb-4">
                    Realistic timelines for every step, tailored to your country of birth and investment category.
                  </p>
                  <Link href="/timeline-calculator">
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid="button-tool-timeline"
                    >
                      See your timeline
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </AnimatedSection>

            {/* Card 3: Visa Bulletin subscription */}
            <AnimatedSection animation="fade-up" delay={200} className="w-full min-w-0">
              <Card className="glass-card h-full flex flex-col" data-testid="card-tool-bulletin">
                <div
                  className="h-24 sm:h-36 bg-[#0A2540] rounded-t-md flex items-center justify-center"
                  aria-hidden="true"
                >
                  <Flame className="h-11 w-11 sm:h-14 sm:w-14 text-[#C9A84C]" />
                </div>
                <CardContent className="p-5 flex flex-col flex-1">
                  <h3 className="font-bold text-base mb-2">Track visa availability monthly</h3>
                  <p className="text-sm text-muted-foreground flex-1 mb-4">
                    We read every monthly Visa Bulletin and send a plain-English summary of what changed and what it means for you.
                  </p>
                  <VisaBulletinSubscribeInline accountEmail={user?.email} />
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 7: FROM THE TEAM (Buffer-published social gallery)
          ================================================================ */}
      {galleryPreview.length > 0 && (
        <section
          className="py-16 sm:py-24 bg-muted/30"
          data-testid="section-content-gallery"
          aria-label="Media"
        >
          <div className="container mx-auto px-4 max-w-6xl">
            <AnimatedSection
              animation="fade-up"
              className="mb-12 max-w-2xl"
            >
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">
                Media
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Videos and posts from our YouTube, LinkedIn, Instagram, and other
                channels.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <ContentGalleryGrid
                items={galleryPreview}
                testIdPrefix="home-gallery"
                compact
              />
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200} className="mt-12">
              <Link href="/media">
                <span
                  className="text-[#0A2540] font-medium hover:text-[#C9A84C] transition-colors inline-flex items-center gap-1.5"
                  data-testid="link-home-media-all"
                >
                  View all media
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* ================================================================
          SECTION 8: FAQ
          ================================================================ */}
      <section
        className={`py-16 sm:py-24 ${galleryPreview.length > 0 ? "bg-background" : "bg-muted/30"}`}
        data-testid="section-faq"
        aria-label="FAQ"
      >
        <div className="container mx-auto px-4 max-w-3xl">
          <AnimatedSection animation="fade-up" className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold">Common questions</h2>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" delay={100}>
            <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
              {FAQ_ITEMS.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border border-border/60 rounded-xl px-5 bg-background"
                  data-testid={`faq-item-${item.id}`}
                >
                  <AccordionTrigger
                    className="text-left font-semibold text-sm sm:text-base py-4 hover:no-underline"
                    data-testid={`faq-trigger-${item.id}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-sm leading-relaxed pb-4"
                    data-testid={`faq-answer-${item.id}`}
                  >
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" delay={200} className="text-center mt-8">
            <Link href="/knowledge-base">
              <span
                className="text-[#C9A84C] font-medium hover:text-[#D9BD6C] transition-colors inline-flex items-center gap-1"
                data-testid="link-faq-more"
              >
                Have more questions? View all FAQs
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================
          SECTION 9: FINAL CTA
          ================================================================ */}
      <section
        className="relative py-20 sm:py-28 text-white overflow-hidden"
        data-testid="section-final-cta"
        aria-label="Book a consultation"
      >
        <div className="absolute inset-0 bg-[#0A2540]" aria-hidden="true">
          <img
            src="/images/home/family-arrival.webp"
            alt=""
            role="presentation"
            width={1920}
            height={900}
            className="w-full h-full object-cover opacity-25"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/80 to-[#0A2540]/95" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <AnimatedSection animation="fade-up">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4"
              data-testid="text-cta-heading"
            >
              Talk to a real person about your family's path
            </h2>
            <p className="text-lg text-white/75 max-w-xl mx-auto mb-8">
              A free 30-minute video consultation with an EB-5 specialist. We will answer your questions, walk through the current project, and map your timeline. No obligation.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/schedule-call">
                <Button
                  size="lg"
                  className="btn-gold-3d text-brand-dark-bg font-semibold h-14 px-8 text-base"
                  data-testid="button-cta-schedule"
                >
                  Schedule a Free Consultation
                </Button>
              </Link>

              <WhatsAppButton
                message={whatsAppMessageForAudience("visa-prospect")}
                label="Prefer WhatsApp? Chat with us"
                source="homepage-final-cta"
                testId="button-cta-whatsapp"
              />
            </div>

            {nextSlot && (
              <p className="mt-4 text-sm text-white/55" data-testid="text-next-available">
                Next available: {nextSlot}
              </p>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================
          SECTION 10: FOOTER
          ================================================================ */}
      <footer className="bg-[#061828] text-white py-12 sm:py-16 texture-dots" data-testid="section-footer">
        <div className="container mx-auto px-4">
          <div className="mb-10">
            <img src="/logo-dark.svg" alt={BRAND.siteName} className="h-12 sm:h-14 w-auto mb-4" />
          </div>

          <div className="glass-card p-6 sm:p-8 mb-8 border border-primary/20" data-testid="disclaimer-section">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-primary mb-2">Important Disclaimer</h3>
                <p className="text-[#E0E0E0]/70 text-sm sm:text-base leading-relaxed">
                  The information provided on this website is for <strong className="text-white">general educational and informational purposes only</strong>.
                  It does not constitute, and should not be relied upon as, legal advice, immigration advice, or investment advice.
                  No attorney-client or professional relationship is created by using this site or its resources.
                </p>
                <p className="text-[#E0E0E0]/70 text-sm sm:text-base leading-relaxed mt-3">
                  EB-5 immigration investment decisions are complex and involve significant financial risk. You should consult with
                  <strong className="text-white"> qualified immigration attorneys, licensed financial advisors, and other professionals</strong> before
                  making any decisions. This website is strictly a resource guide and makes no representations about the accuracy,
                  completeness, or suitability of any information provided.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-heading font-semibold text-primary mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link href="/visa-bulletin" className="text-white/60 hover:text-white transition-colors">Visa Bulletin</Link></li>
                <li><Link href="/source-of-funds" className="text-white/60 hover:text-white transition-colors">Source of Funds</Link></li>
                <li><Link href="/eb-5-regional-center/rural-tea" className="text-white/60 hover:text-white transition-colors">Rural TEA</Link></li>
                <li><Link href="/knowledge-base" className="text-white/60 hover:text-white transition-colors">Knowledge Base</Link></li>
                <li><Link href="/legislation" className="text-white/60 hover:text-white transition-colors">Legislation</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-primary mb-4">Investment Paths</h4>
              <ul className="space-y-2 text-sm">
                <li><Link href="/direct-eb5" className="text-white/60 hover:text-white transition-colors">Direct vs Regional</Link></li>
                <li><Link href="/gold-card" className="text-white/60 hover:text-white transition-colors">Gold Card vs EB-5</Link></li>
                <li><Link href="/regional-centers" className="text-white/60 hover:text-white transition-colors">Regional Centers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-primary mb-4">Community</h4>
              <ul className="space-y-2 text-sm">
                <li><Link href="/media" className="text-white/60 hover:text-white transition-colors" data-testid="link-footer-media">Media</Link></li>
                <li><Link href="/forum" className="text-white/60 hover:text-white transition-colors">Forum</Link></li>
                <li><Link href="/rc-reviews" className="text-white/60 hover:text-white transition-colors">RC Reviews</Link></li>
                <li><Link href="/countries" className="text-white/60 hover:text-white transition-colors">Country Guides</Link></li>
                <li><Link href="/agents/lead-referral" className="text-white/60 hover:text-white transition-colors" data-testid="link-become-referral-partner">Become a Referral Partner</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-primary mb-4">Invest</h4>
              <ul className="space-y-2 text-sm">
                <li><Link href="/projects" className="text-white/60 hover:text-white transition-colors">Projects</Link></li>
                <li><Link href="/schedule-call" className="text-white/60 hover:text-white transition-colors">Schedule a Consultation</Link></li>
                <li><Link href="/investor-verification" className="text-white/60 hover:text-white transition-colors">Confirm Eligibility</Link></li>
                <li><Link href="/sitemap" className="text-white/60 hover:text-white transition-colors" data-testid="link-footer-sitemap">All Pages</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/15 pt-8 text-center">
            <p className="text-white/75 text-sm">
              &copy; {new Date().getFullYear()} {BRAND.siteName}. {BRAND.siteName} is an EB-5 investor visa platform. Not affiliated with USCIS or any government agency.
            </p>
            <p className="text-white/75 text-xs mt-2 max-w-4xl mx-auto" data-testid="text-site-footer-disclaimer">
              {SITE_FOOTER_DISCLAIMER}
            </p>
            <p className="text-white/65 text-xs mt-2">
              This site does not provide legal, immigration, or investment advice. Consult qualified professionals for guidance.
              {/* TODO: securities counsel to confirm final exemption language per live offering */}
              {" "}Securities are offered only through formal offering documents, only to investors who meet applicable eligibility requirements under U.S. securities laws, including verified accredited investors in the United States and eligible non-U.S. persons.
            </p>
            <SecuritiesDisclaimer className="text-white/45 text-xs mt-4 max-w-4xl mx-auto text-left" />
            <p className="text-white/45 text-xs mt-3">
              <Link href="/terms" className="hover:text-white/70 transition-colors">Terms of Service</Link>
              {" · "}
              <Link href="/privacy" className="hover:text-white/70 transition-colors">Privacy Policy</Link>
              {" · "}
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("open-cookie-preferences"));
                }}
                className="hover:text-white/70 transition-colors cursor-pointer underline-offset-2 hover:underline"
                data-testid="link-footer-cookie-preferences"
              >
                Cookie preferences
              </button>
            </p>
          </div>
        </div>
      </footer>

      {/* Lead capture modal */}
      <InvestorPackageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        projectId={featuredProject?.id ?? null}
        accountEmail={user?.email}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeaturedProjectBlock: full-width single-project spotlight
// ---------------------------------------------------------------------------

// Turn a raw enum/slug value ("rural_tea", "real-estate") into a readable
// label ("Rural TEA", "Real Estate") for the spotlight stat boxes.
function humanizeLabel(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bTea\b/g, "TEA")
    .replace(/\bEb ?5\b/gi, "EB-5")
    .replace(/\bUscis\b/gi, "USCIS")
    .trim();
}

// Build safe, generic spotlight stats from a project's own real data. Used for
// any featured project that doesn't have hand-written deal-specific copy, so we
// never show one deal's claims on another deal's card.
function deriveGenericStats(project: ProjectTeaser): ProjectStat[] {
  const out: ProjectStat[] = [];
  if (typeof project.investmentMinimum === "number" && project.investmentMinimum > 0) {
    out.push({ value: formatCurrency(project.investmentMinimum), label: "Minimum EB-5 Investment" });
  }
  if (project.category) {
    out.push({ value: humanizeLabel(project.category), label: "Project Category" });
  }
  if (project.investmentType) {
    out.push({ value: humanizeLabel(project.investmentType), label: "Investment Structure" });
  }
  if (project.location) {
    out.push({ value: project.location, label: "Project Location" });
  }
  return out.slice(0, 4);
}

function FeaturedProjectBlock({
  project,
  copy,
  onRequestPackage,
  projectPageHref,
}: {
  project: ProjectTeaser;
  copy: ProjectMarketingCopy | null;
  onRequestPackage: () => void;
  projectPageHref: string | null;
}) {
  const headline = copy?.headline ?? project.title;
  const subhead = copy?.subhead ?? project.subtitle ?? "";

  const projectImage =
    pickProjectImage(project, "cover_thumbnail") ??
    pickProjectImage(project, "cover_header") ??
    project.coverImageUrl ??
    null;

  // Deal-specific stats only render for the project they belong to; everyone
  // else gets generic stats derived from their own real fields.
  const stats = copy?.stats ?? deriveGenericStats(project);

  const highlight =
    copy?.highlight ??
    (typeof project.eb5RaiseTarget === "number" && project.eb5RaiseTarget > 0
      ? { value: formatCurrency(project.eb5RaiseTarget), label: "Total EB-5 Allocation" }
      : null);

  const positionsNote = copy?.positionsNote ?? null;

  return (
    <div>
      {/* Eyebrow */}
      <div className="text-center mb-8">
        <span className="inline-block text-[#C9A84C] text-sm font-semibold mb-2">
          Featured Project
        </span>
      </div>

      {/* Split layout */}
      <div
        className="grid lg:grid-cols-[55fr_45fr] gap-0 rounded-2xl overflow-hidden border border-border/60 shadow-xl"
        data-testid="featured-project-layout"
      >
        {/* Left: Image stack */}
        <div className="relative min-w-0 bg-[#0A2540] min-h-[320px] lg:min-h-[520px]">
          {projectImage ? (
            <img
              src={projectImage}
              alt={`${project.title} project site, ${project.location}`}
              width={960}
              height={720}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              data-testid={`img-featured-project-${project.id}`}
            />
          ) : (
            <div className="w-full h-full min-h-[320px] flex flex-col items-center justify-center gap-3 p-8 text-center">
              <Building2 className="h-14 w-14 text-[#C9A84C]/70" aria-hidden="true" />
              <span className="text-sm font-medium text-white/70">{project.title}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20 lg:block hidden" />
        </div>

        {/* Right: Content */}
        <div className="min-w-0 bg-background p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
          {isApproved(project.i956fApprovedAt) && (
            <div className="mb-4">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-blue-500/10 text-blue-700 border border-blue-500/20"
                data-testid="badge-featured-uscis-approved"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                USCIS Approved (I-956F)
              </span>
            </div>
          )}
          <h2
            className="text-2xl sm:text-3xl font-bold leading-tight mb-3 break-words"
            data-testid="text-featured-headline"
          >
            {headline}
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6" data-testid="text-featured-subhead">
            {subhead}
          </p>

          {/* Stat boxes: 2x2 grid */}
          {stats.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              {stats.map((stat, i) => (
                <div
                  key={`${stat.label}-${i}`}
                  className="min-w-0 rounded-xl border border-[rgba(201,168,76,0.3)] bg-[rgba(201,168,76,0.05)] p-3"
                  data-testid={`stat-${stat.value.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  <p className="font-bold text-lg text-[#C9A84C] break-words">{stat.value}</p>
                  <p className="text-xs text-muted-foreground leading-snug break-words">{stat.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Highlight stat as badge */}
          {highlight && (
            <div className="mb-2">
              <Badge
                variant="secondary"
                className="text-xs"
                data-testid="stat-track-record"
              >
                {highlight.value}: {highlight.label}
              </Badge>
            </div>
          )}

          {positionsNote && (
            <p className="text-sm font-semibold text-foreground mb-6" data-testid="text-featured-positions">
              {positionsNote}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {projectPageHref ? (
              <Link href={projectPageHref}>
                <Button
                  className="btn-gold-3d text-brand-dark-bg font-semibold w-full sm:w-auto"
                  data-testid="button-featured-view-details"
                >
                  View Project Details
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            ) : (
              // TODO: link to /projects/american-energy once the public project page is built
              <Button
                className="btn-gold-3d text-brand-dark-bg font-semibold w-full sm:w-auto"
                onClick={() => {
                  document
                    .getElementById("featured-project")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="button-featured-view-details"
              >
                View Project Details
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={onRequestPackage}
              data-testid="button-featured-request-package"
            >
              Request the Investor Package
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-4 leading-relaxed" data-testid="text-featured-disclaimer">
            This is not an offer to sell securities. Detailed offering materials are available only to qualified investors through the formal offering documents.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VisaBulletinSubscribeInline: wires to leads endpoint with source
// visa_bulletin_subscribe
// ---------------------------------------------------------------------------

function VisaBulletinSubscribeInline({ accountEmail: accountEmailProp }: { accountEmail?: string | null }) {
  const accountEmail = knownAccountEmail(accountEmailProp);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const submitLock = useSubmitLock(500);

  const mutation = useMutation({
    mutationFn: async () => {
      const utm = getUtmData();
      await apiRequest("POST", "/api/inquiries", {
        name: "Visa bulletin subscriber",
        email: accountEmail ?? email,
        phone: "",
        country: "",
        message: "",
        source: "visa_bulletin_subscribe",
        ...utm,
        ...analyticsVisitorFields(),
      });
    },
    onSuccess: () => setSubmitted(true),
    onError: () => setError("Something went wrong. Please try again."),
  });

  function handleSubmit() {
    if (!accountEmail && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    if (mutation.isPending || submitLock.locked) return;
    void submitLock.run(() => mutation.mutateAsync());
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400" data-testid="bulletin-subscribe-success">
        <CheckCircle className="h-4 w-4 shrink-0" />
        <span>You're subscribed. We'll send the next bulletin summary.</span>
      </div>
    );
  }

  return (
    <div data-testid="bulletin-subscribe-form">
      <div className="flex flex-wrap gap-2">
        {accountEmail ? null : (
          <input
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            placeholder="your@email.com"
            className="flex-1 min-w-0 basis-36 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="input-bulletin-email"
            aria-label="Email address for Visa Bulletin updates"
          />
        )}
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={mutation.isPending || submitLock.locked}
          className="btn-gold-3d text-brand-dark-bg font-semibold shrink-0"
          data-testid="button-bulletin-subscribe"
        >
          {mutation.isPending ? "..." : "Get monthly updates"}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-destructive mt-1" data-testid="error-bulletin-email">{error}</p>
      )}
    </div>
  );
}


